import React from "react";
import { styled, Box, Typography, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutlined as SuccessIcon,
} from "@mui/icons-material";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
  FONT_FAMILIES,
} from "../../constants/styles";

// Alert 타입 정의
export type AlertType = "success" | "warning" | "error" | "info";

/**
 * 인라인 알림 배너 props — `type` 별로 색상/아이콘이 자동 매핑된다.
 *
 * 자동으로 사라지는 토스트가 필요하면 `notice()` (common-utils/utils) 를 쓴다.
 */
export interface AlertProps {
  /**
   * Alert 타입
   */
  type: AlertType;
  /**
   * Alert 제목
   */
  title?: string;
  /**
   * Alert 내용
   */
  message: string;
  /**
   * 닫기 버튼 표시 여부
   */
  closable?: boolean;
  /**
   * 닫기 버튼 클릭 핸들러
   */
  onClose?: () => void;
  /**
   * 액션 라벨 텍스트.
   *
   * 액션 버튼의 접근명은 `"{actionLabel}: {message}"` 로 합성된다 — 한 화면에 Alert 이
   * 여럿 뜰 때 액션끼리 접근명이 겹치지 않게 하기 위함이다. 그러므로 대상 구별은 호출부의
   * `message` 가 인스턴스마다 다른지에 달려 있다(같은 컴포넌트를 여러 개 렌더하는 화면은
   * 문구에 대상 이름을 보간한다).
   */
  actionLabel?: string;
  /**
   * 액션 라벨 클릭 핸들러
   */
  onActionClick?: () => void;
  /**
   * 추가 CSS 클래스명
   */
  className?: string;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// Alert 타입별 스타일 정의 (Figma 디자인 기반)
const getAlertStyles = (type: AlertType) => {
  switch (type) {
    case "success":
      return {
        backgroundColor: COLOR_CONSTANTS.alert.success.background,
        iconColor: COLOR_CONSTANTS.success.main,
        titleColor: COLOR_CONSTANTS.alert.success.color,
        messageColor: COLOR_CONSTANTS.alert.success.color,
      };
    case "warning":
      return {
        backgroundColor: COLOR_CONSTANTS.alert.warning.background,
        iconColor: COLOR_CONSTANTS.warning.main,
        titleColor: COLOR_CONSTANTS.alert.warning.color,
        messageColor: COLOR_CONSTANTS.alert.warning.color,
      };
    case "error":
      return {
        backgroundColor: COLOR_CONSTANTS.alert.error.background,
        iconColor: COLOR_CONSTANTS.error.main,
        titleColor: COLOR_CONSTANTS.alert.error.color,
        messageColor: COLOR_CONSTANTS.alert.error.color,
      };
    case "info":
      return {
        backgroundColor: COLOR_CONSTANTS.alert.info.background,
        iconColor: COLOR_CONSTANTS.info.main,
        titleColor: COLOR_CONSTANTS.alert.info.color,
        messageColor: COLOR_CONSTANTS.alert.info.color,
      };
    default:
      return {
        backgroundColor: COLOR_CONSTANTS.alert.info.background,
        iconColor: COLOR_CONSTANTS.info.main,
        titleColor: COLOR_CONSTANTS.alert.info.color,
        messageColor: COLOR_CONSTANTS.alert.info.color,
      };
  }
};

// Styled Container
const StyledContainer = styled(Box)<{ type: AlertType }>(({ type }) => {
  const styles = getAlertStyles(type);

  return {
    display: "flex",
    alignItems: "flex-start",
    gap: `${SPACING_CONSTANTS.spacing[3]}px`,
    padding: `${SPACING_CONSTANTS.spacing[4]}px`,
    borderRadius: SPACING_CONSTANTS.borderRadius.lg,
    backgroundColor: styles.backgroundColor,
    fontFamily: FONT_FAMILIES.primary,
    position: "relative",
    transition: "all 0.2s ease-in-out",

    // 호버 상태
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    },
  };
});

/** 알림 아이콘 크기 — 아이콘 모양만 type 별로 다르고 크기는 type 과 무관한 공통 값이다. */
const ALERT_ICON_SIZE = "22px";

// Styled Icon Container
const StyledIconContainer = styled(Box)<{ type: AlertType }>(({ type }) => {
  const styles = getAlertStyles(type);

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: `${SPACING_CONSTANTS.spacing[1] / 2}px`,
  };
});

// Styled Content Container
const StyledContentContainer = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: `${SPACING_CONSTANTS.spacing[1]}px`,
});

/**
 * 알림 글줄 높이 — 제목·본문·액션 라벨이 함께 읽는다. 액션 라벨은 내용 열(제목·본문)과 같은 행에
 * 위쪽 정렬로 서므로, 셋이 같은 값이어야 라벨이 첫 글줄(제목, 없으면 본문)과 기준선이 맞는다.
 */
const ALERT_TEXT_LINE_HEIGHT = "20px";

// Styled Title
const StyledTitle = styled(Typography)<{ type: AlertType }>(({ type }) => {
  const styles = getAlertStyles(type);

  return {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: 600,
    fontSize: FONT_SIZES.md,
    lineHeight: ALERT_TEXT_LINE_HEIGHT,
    color: styles.titleColor,
    margin: 0,
  };
});

// Styled Message
const StyledMessage = styled(Typography)<{ type: AlertType }>(({ type }) => {
  const styles = getAlertStyles(type);

  return {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: 400,
    fontSize: FONT_SIZES.md,
    lineHeight: ALERT_TEXT_LINE_HEIGHT,
    color: styles.messageColor,
    margin: 0,
    whiteSpace: "pre-line",
  };
});

// Styled Close Button
const StyledCloseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "alertType",
})<{ alertType: AlertType }>(({ alertType }) => {
  const styles = getAlertStyles(alertType);

  return {
    position: "absolute",
    top: `${SPACING_CONSTANTS.spacing[2]}px`,
    right: `${SPACING_CONSTANTS.spacing[2]}px`,
    width: "24px",
    height: "24px",
    padding: 0,
    color: styles.titleColor,

    "&:hover": {
      backgroundColor: COLOR_CONSTANTS.COLOR.GREY[50],
    },
  };
});

// Styled Action Label
// 실제 <button> 이다 — role/tabIndex/키 핸들러를 손으로 붙이지 않아도 키보드로 도달·실행된다.
// prop 이름이 `alertType` 인 이유: `type` 은 네이티브 button 의 예약 속성이라 충돌한다
// (같은 파일 StyledCloseButton 과 동일 관례).
const StyledActionLabel = styled("button", {
  shouldForwardProp: (prop) => prop !== "alertType",
})<{ alertType: AlertType }>(
  ({ alertType }) => {
    const styles = getAlertStyles(alertType);

    return {
      fontFamily: FONT_FAMILIES.primary,
      fontWeight: 500,
      fontSize: FONT_SIZES.md,
      lineHeight: ALERT_TEXT_LINE_HEIGHT,
      color: styles.titleColor,
      cursor: "pointer",
      // 실제 <button> 으로 렌더하므로(키보드 도달·Enter/Space 가 네이티브로 동작한다)
      // 브라우저 기본 버튼 외양만 지운다.
      background: "none",
      border: "none",
      padding: 0,
      textAlign: "inherit",
      textUnderlineOffset: `${SPACING_CONSTANTS.spacing[1] / 2}px`,
      transition: "all 0.2s ease-in-out",
      flexShrink: 0,
      marginLeft: "auto",
      marginRight: `${SPACING_CONSTANTS.spacing[8]}px`, // 닫기 버튼과의 간격 확보

      "&:hover": {
        opacity: 0.8,
      },

      "&:focus-visible": {
        outline: `2px solid ${styles.titleColor}`,
        outlineOffset: `${SPACING_CONSTANTS.spacing[1] / 2}px`,
        borderRadius: SPACING_CONSTANTS.borderRadius.xs,
      },
    };
  }
);

/**
 * Alert 컴포넌트
 *
 * 4가지 타입의 알림 컴포넌트입니다.
 * 각 타입별로 고유한 색상과 스타일을 가집니다.
 * Figma 디자인 시스템을 기반으로 구현되었습니다.
 */
export const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  closable = false,
  onClose,
  actionLabel,
  onActionClick,
  className,
  ...props
}) => {
  const styles = getAlertStyles(type);

  // 통지의 긴급도를 보조 기술에 전달한다 — 조회 실패 안내는 사용자가 재시도해야 하므로
  // 즉시 알리고(alert), 정보성 통지는 흐름을 끊지 않게 대기열에 넣는다(status).
  const liveRole = type === "error" || type === "warning" ? "alert" : "status";

  // 타입별 아이콘 렌더링 — 분기는 아이콘 컴포넌트만 고르고, sx(공통 크기 + 타입 색)는 한 벌을 함께 쓴다.
  const renderIcon = () => {
    const iconSx = { fontSize: ALERT_ICON_SIZE, color: styles.iconColor };

    switch (type) {
      case "success":
        return <SuccessIcon sx={iconSx} />;
      case "warning":
        return <WarningIcon sx={iconSx} />;
      case "error":
        return <ErrorIcon sx={iconSx} />;
      case "info":
        return <InfoIcon sx={iconSx} />;
      default:
        return null;
    }
  };

  return (
    <StyledContainer type={type} className={className} role={liveRole} {...props}>
      <StyledIconContainer type={type}>{renderIcon()}</StyledIconContainer>

      <StyledContentContainer>
        {title && (
          <StyledTitle type={type} variant="body2">
            {title}
          </StyledTitle>
        )}
        <StyledMessage type={type} variant="body2">
          {message}
        </StyledMessage>
      </StyledContentContainer>

      {actionLabel && onActionClick && (
        // 접근명은 `actionLabel`(가시 텍스트) **로 시작**하고 뒤에 자기 `message` 를 붙인다.
        // - 시작 위치를 지키는 이유: 접근명이 가시 텍스트를 포함해야 음성 제어 사용자가
        //   보이는 대로("다시 시도") 말했을 때 매칭된다(WCAG 2.5.3 Label in Name).
        // - 뒤에 붙이는 값이 `message` 인 이유: 한 화면에 대상이 다른 Alert 이 여럿 뜨면
        //   (조회 실패 게이트로 흔한 배치다) `actionLabel` 만으로는 모든 액션의 접근명이
        //   바이트 단위로 같아져 어느 재시도가 무엇을 부르는지 구별할 수단이 사라진다.
        // - 로케일 무관한 하드코딩 접미어("… 액션 실행")를 다시 넣지 않는다. 결합 대상은
        //   호출부에서 이미 i18n 을 거친 `message` 값뿐이다(구분자는 문장부호).
        //   대상까지 구별되려면 호출부의 `message` 자체가 인스턴스마다 달라야 한다.
        <StyledActionLabel
          alertType={type}
          type="button"
          onClick={onActionClick}
          aria-label={`${actionLabel}: ${message}`}
        >
          {actionLabel}
        </StyledActionLabel>
      )}

      {closable && onClose && (
        <StyledCloseButton alertType={type} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </StyledCloseButton>
      )}
    </StyledContainer>
  );
};

export default Alert;
